/* eslint-disable prettier/prettier */
import { ContentType } from '../ContentType';
import { Messages } from '../../../../gen-src/Localize';
import { AppUrls } from '../../../app/AppUrls';
import { ConnectedOwnerFilter } from '../../components/filters/quickFilter/OwnerFilter';
import { IContentDefinition } from './IContentDefinition';
import { ConnectedMetricCard } from '../../components/CardView/ContentCard/ConnectedMetricCard';
import { ConnectedModifiedTimeFilter } from '../../components/filters/quickFilter/ModifiedTimeFilter';
import { GetMetricsResult } from '../httpClient/MetaDataResults';
import { SearchResponseHitsContent } from '../../../generated/content-exploration';
import { IUser } from '../IUser';
import { UserMapper } from '../../../common/utils/UserMapper';
import { SessionManagementService } from '../../services/SessionManagementService';
import GetMetaDataRequests from '../../services/httpClient/GetMetaDataRequests';
import { Metric } from '../SearchResultTypes';
import { LocationTypes } from '../ILocationInfo';
import { SortBy } from '../SortSelection';
import { FeatureFlag, FeatureFlagService } from '../../services/FeatureFlagService';
import { CommonColumns, IColumnFactoryParameters } from '../../components/listView/ListViewGridColumnGenerator';
import { defaultHeaderFormatter } from '../../components/listView/DefaultHeaderFormatter';
import { LocalizedCountCell } from '../../components/listView/dataGridCells/PlainTextAndNumberCells';
import { DateTimeCell } from '../../components/listView/dataGridCells/DateTimeCells';
import { OrderKeys } from '../sort/OrderKeys';
import { MetricValueCell, MetricUpdatedCell } from '../../components/listView/dataGridCells/MetricCells';
import { ConnectedTagFilter } from '../../components/filters/quickFilter/TagFilter';

export const metricContentDefinition: IContentDefinition<Metric, GetMetricsResult> = {
  getSectionTitle: () => Messages.MetricSectionTitle(),
  getUrl: (urls) => urls.searchMetricsUrl(),
  getPath: (urls) => urls.searchMetricsPath(),
  mapCRASSData: (crassData: SearchResponseHitsContent[]) => {
    return crassData.map((crassDataItem: SearchResponseHitsContent) => {
      return {
        crassData: crassDataItem,
        contentType: ContentType.metric,
        name: crassDataItem.title,
        id: crassDataItem.id.toString(),
        url: new AppUrls({
          siteUrlName: SessionManagementService.instance.sessionSiteUrlName(),
          queryString: window.location.search,
        }).redirectToMetricUrl(crassDataItem.id.toString()),
        modifiedTime: crassDataItem.modifiedTime || '',
        suspended: crassDataItem.suspendState !== 'NotSuspended',
        description: crassDataItem.description || '',
        tags: crassDataItem.tags || [],
        hitsTotal: crassDataItem.hitsTotal,
        owner: crassDataItem.ownerId
          ? UserMapper.mapOne({
            id: crassDataItem.ownerId?.toString(),
            displayName: crassDataItem.ownerName,
            domainName: crassDataItem.ownerDomain || '',
            username: crassDataItem.ownerAlias || '',
          } as IUser)
          : UserMapper.mapOne(undefined),
        location: crassDataItem.containerName
          ? {
            name: crassDataItem.containerName,
            id: crassDataItem.projectId?.toString(),
            type: LocationTypes.Project, //Metrics can not be in a personal space
          }
          : undefined,
      };
    }) as Metric[];
  },
  mapMetadata: (searchResultItems, metadata) => {
    const metaDataMetrics: { [metricId: string]: boolean } = {};

    if (metadata.total > 0) {
      metadata.items.favorites.forEach((id) => {
        metaDataMetrics[id] = true;
      });
    }
    return searchResultItems.map((searchResultItem) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const crassDataItem = searchResultItem.crassData!;
      return {
        ...searchResultItem,
        favorite: metaDataMetrics[crassDataItem.id.toString()] ? metaDataMetrics[crassDataItem.id.toString()] : false,
      };
    });
  },
  getListViewColumns: () => [
    CommonColumns.name,
    CommonColumns.owner,
    CommonColumns.location,
    (parameters: IColumnFactoryParameters) => {
      return {
        name: OrderKeys.hitsTotal,
        dataKey: OrderKeys.hitsTotal,
        sortable: false,
        direction: 'asc',
        headerText: Messages.column_hitsTotal(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LocalizedCountCell((data: Metric) => data.hitsTotal),
        maxColumnWidth: 10,
      };
    },
    (parameters: IColumnFactoryParameters) => {
      return {
        name: OrderKeys.metricValue,
        dataKey: OrderKeys.metricValue,
        headerText: Messages.column_metrics_value(),
        sortable: false,
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: MetricValueCell(),
      };
    },
    (parameters: IColumnFactoryParameters) => {
      return {
        name: OrderKeys.updatedAt,
        dataKey: OrderKeys.updatedAt,
        sortable: false,
        headerText: Messages.column_metrics_lastUpdated(),
        direction: 'asc',
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: MetricUpdatedCell(),
      };
    },
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'modifiedTime',
        dataKey: 'modifiedTime',
        sortable: false,
        headerText: Messages.column_updated(),
        direction: 'desc',
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: DateTimeCell('modifiedTime'),
        ...{ growToFit: true },
      };
    },
  ],
  defaultDisplayMode: 'thumbnail',
  cardComponent: ConnectedMetricCard,
  maxItemCountInMixedContentTypeResultPage: 4,
  contentSpaceUrl: (urls: AppUrls, contentId: string) => urls.metricUrl(contentId),
  quickFilters: [ConnectedOwnerFilter, ConnectedModifiedTimeFilter, ConnectedTagFilter],
  getMetaDataRequests: GetMetaDataRequests.GetMetricsRequest,
  getSortOptions: () => [SortBy.getRelevancySortSelection(), SortBy.getPopularitySortSelection()],
  shouldBeAvailable: () => (FeatureFlagService.instance.getValue(FeatureFlag.metricsSpaceEnabled) && SessionManagementService.instance.metricsOnSiteEnabled()),
  favoritable: () => true,
};
